import { AsyncThunkPayloadCreator, createAsyncThunk } from '@reduxjs/toolkit';
import { debounce } from 'lodash';
import { replace } from 'connected-react-router';

import { ACTION_BASE } from '../constants';
import { VisualAppState } from '../../reducer';
import { parseSavedFilters, serializeFilters } from './utils';
import * as selectors from './selectors';
import { VISUAL_SLICE_NAME } from './constants';
import { SetFilterBarStatePayload } from './types';

const NAMESPACE = `${ACTION_BASE}/${VISUAL_SLICE_NAME}`;

export const updateBuildDetailsSearch = createAsyncThunk(
  `${NAMESPACE}/updateBuildDetailsSearch`,
  (value: string) => (
    value
  ),
);

/**
 * Debounced handler exclusively for triggering our _actual_ thunk to update the search value.
 * Needs to be done separately, so we can handle the promise result for the correct (above) one.
 */
export const asyncUpdateBuildDetailsSearch = createAsyncThunk(
  `${NAMESPACE}/asyncUpdateBuildDetailsSearch`,
  debounce<AsyncThunkPayloadCreator<void, string>>(
    (
      (value, thunkAPI) => {
        void thunkAPI.dispatch(updateBuildDetailsSearch(value));
      }
    ),
    600, { trailing: true, leading: false },
  ),
);

export const updateSearchField = createAsyncThunk(
  `${NAMESPACE}/updateSearchField`,
  (value: string, thunkAPI) => {
    void thunkAPI.dispatch(asyncUpdateBuildDetailsSearch(value));
  },
);

export const initFilters = createAsyncThunk(
  `${NAMESPACE}/initBuildDetailsFilters`,
  (
    payload: {
      filterValues: SetFilterBarStatePayload,
      project: string | null | undefined,
    }, thunkAPI,
  ) => {
    const { filterValues, project } = payload;
    const state = thunkAPI.getState() as VisualAppState;

    const savedFilterState = parseSavedFilters(
      selectors.location(state).search,
      project,
    );

    return {
      project,
      payload: filterValues,
      params: savedFilterState,
    };
  },
);

export const saveFilters = createAsyncThunk(
  `${NAMESPACE}/saveBuildDetailsFilters`,
  (__, thunkAPI) => {
    const state = thunkAPI.getState() as VisualAppState;

    const filterRoot = selectors.filterRoot(state);
    const serializedFilters = serializeFilters(
      filterRoot,
    );

    // Only persist state into localStorage if a user has made a manual action to update state
    if (filterRoot.userUpdated) {
      let allValues;

      try {
        allValues = JSON.parse(localStorage.getItem(VISUAL_SLICE_NAME) ?? '{}');
      } catch (___) {
        allValues = {};
      }

      allValues[filterRoot.project] = serializedFilters;
      localStorage.setItem(VISUAL_SLICE_NAME, JSON.stringify(allValues));
    }

    const location = selectors.location(state);

    thunkAPI.dispatch(replace({
      ...location,
      search: new URLSearchParams(serializedFilters).toString(),
    }));
  },
);
